import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import { Box, Link, Paper, Typography } from '@mui/material';

/** Material UI Icons. */
import TagFacesIcon from '@mui/icons-material/TagFaces';
import LanguageIcon from '@mui/icons-material/Language';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Redux Packages. */
import { useDispatch } from 'react-redux';

/** Redux Reducers. */
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Static Assets. */
import drayage from 'statics/images/insight_drayage.png';
import pricing from 'statics/images/insight_pricing.png';
import smartSystem from 'statics/images/insight_smart_system.png';

/** Internal Components. */
import { customized_style } from 'shared/config/Styles';
import { callAPI } from 'shared/api/callApi';
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import useUserRole from 'shared/hooks/useUserRole';

/** Profile Info Row Component. */
const ProfileInfoRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', mb: 2 }}>
        <Typography sx={{ width: '90px', fontSize: '14px', fontWeight: 500, color: 'primary.dark' }}>
            {label}
        </Typography>
        <Typography>{value || '-'}</Typography>
    </Box>
);

/**
 * Hover Card Component.
 * Displays an image card with a hover effect that scales up the image and clicks the card navigates to the provided link.
 */
const HoverCard = ({ image, title, link }) => (
    <Link href={link || '#'} target="_blank">
        <Paper
            sx={{
                borderRadius: '18px',
                overflow: 'hidden',
                width: '31vw',
                aspectRatio: '16 / 9',
                boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.33)',
                position: 'relative',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${image})`,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    transition: 'transform 0.3s ease-in-out',
                    zIndex: 0,
                    transform: 'scale(1)',
                },
                '&:hover::before': {
                    transform: 'scale(1.1)',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    borderRadius: 'inherit',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Typography sx={{ fontSize: '17px', fontWeight: 'bold', color: '#fff' }}>{title}</Typography>
            </Box>
        </Paper>
    </Link>
);

export default function MyProfile() {
    /** Retrieve authentication details from Auth0. */
    const { user, getAccessTokenSilently } = useAuth0();

    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State to store user profile information. */
    const [userInfo, setUserInfo] = useState([]);
    /** State to track whether data has been loaded. */
    const [loaded, setLoaded] = useState(false);

    /** Extract user roles that either start with 'Operation' or include 'Administrator'. */
    const userRole = useUserRole()?.filter((role) => role.startsWith('Operation') || role.includes('Administrator'));

    /** Named website title. */
    useEffect(() => {
        document.title = 'My Profile | Operation System';
    }, []);

    /** Fetch user profile data from the API upon component mount. */
    useEffect(() => {
        callAPI({
            method: 'get',
            module: 'myprofile',
            getAccessTokenSilently,
            email: user.email,
            onSuccess: (resp) => {
                setUserInfo(resp.data);
                setLoaded(true);
            },
            onError: () =>
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Something went wrong, please try again.',
                        severity: 'error',
                    })
                ),
        });
    }, [getAccessTokenSilently]);

    return (
        <>
            {loaded ? (
                <Paper sx={customized_style.itemPaper}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TagFacesIcon sx={{ mr: 1, color: '#003a52' }} />
                        <Typography sx={{ fontSize: '17px', color: '#003a52', fontWeight: 500 }}>MY PROFILE</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 3 }}>
                        Profile information will be updated automatically.
                    </Typography>
                    <Paper
                        sx={{
                            width: '31.5vw',
                            borderRadius: '10px',
                            mb: 2,
                            boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
                        }}
                    >
                        <Typography
                            sx={{
                                bgcolor: '#dfdfdf',
                                borderTopLeftRadius: '10px',
                                borderTopRightRadius: '10px',
                                px: 2,
                                py: 1,
                                fontSize: 16,
                                fontWeight: 500,
                                color: '#003a52',
                            }}
                        >
                            Information
                        </Typography>
                        <Box sx={{ p: 2 }}>
                            <ProfileInfoRow label="ID" value={userInfo.id} />
                            <ProfileInfoRow label="Name" value={userInfo.name} />
                            <ProfileInfoRow label="User Role" value={userRole} />
                            <ProfileInfoRow label="Team" value={userInfo.il_team?.join(', ') || null} />
                            <ProfileInfoRow label="Email" value={userInfo.email} />
                        </Box>
                    </Paper>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mt: 5,
                            mb: 1,
                        }}
                    >
                        <LanguageIcon sx={{ mr: 1, color: '#003a52' }} />
                        <Typography sx={{ fontSize: '17px', color: '#003a52', fontWeight: 500 }}>OUR SITES</Typography>
                    </Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, mb: 3 }}>Explore our other sites.</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <HoverCard image={drayage} title="Drayage" link="https://drayage.insightshipping.io/" />
                        <HoverCard image={pricing} title="Pricing" />
                        <HoverCard image={smartSystem} title="Smart System" link="https://smart.insightshipping.io/" />
                    </Box>
                </Paper>
            ) : (
                <CustomSkeleton2 />
            )}
        </>
    );
}
